// components/ChatBubbleBase.tsx

import { UserAvatar } from "@/components/shared";
import { ChatMessageWithMentions } from "@/components/shared/mentions";
import { Card } from "@/components/ui/card";
import { BidDecision, MarginDecision, User, UserOnEntity } from "@/graphql/__generated__/graphql";
import { formatDateLocale } from "@/utils/helpers";
import { JSX } from "react";
import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";
import "react-lazy-load-image-component/src/effects/opacity.css";
import { cn } from "@/lib/utils";

interface ChatBubbleBaseProps {
  decision: BidDecision | MarginDecision;
  getDecisionColour: (_decision: BidDecision | MarginDecision) => string;
  getBackgroundColour: (_decision: BidDecision | MarginDecision) => string;
  getTitleTextPrefix: (_decision: BidDecision | MarginDecision) => string;
  getComment: (_decision: BidDecision | MarginDecision) => string;
  getDate: (_decision: BidDecision | MarginDecision) => string;
  getUserName: (_decision: BidDecision | MarginDecision) => string;
  getOrganizationName: (_decision: BidDecision | MarginDecision) => string;
  getUser: (_decision: BidDecision | MarginDecision) => User;
  additionalContent?: React.ReactNode;
  tenderUsers?: UserOnEntity[];
};

/**
 * ChatBubbleBase component renders a chat bubble with user information,
 * decision details, and additional content.
 *
 * @param {ChatBubbleBaseProps} props - The properties for the ChatBubbleBase component.
 * @param {any} props.decision - The decision object containing details to be displayed.
 * @param {function} props.getDecisionColour - Function to get the color based on the decision.
 * @param {function} props.getTitleTextPrefix - Function to get the title text prefix based on the decision.
 * @param {function} props.getComment - Function to get the comment text based on the decision.
 * @param {function} props.getDate - Function to get the date based on the decision.
 * @param {function} props.getUserName - Function to get the user's name based on the decision.
 * @param {function} props.getOrganizationName - Function to get the organization's name based on the decision.
 * @param {function} props.getUser - Function to get the user based on the decision.
 * @param {JSX.Element} props.additionalContent - Additional content to be displayed in the chat bubble.
 * @param {UserOnEntity[]} props.tenderUsers - Optional array of users for mention transformation.
 *
 * @returns {JSX.Element} The rendered ChatBubbleBase component.
 */
const ChatBubbleBase = ({
  decision,
  getDecisionColour,
  getBackgroundColour,
  getTitleTextPrefix,
  getComment,
  getDate,
  getUserName,
  getOrganizationName,
  getUser,
  additionalContent,
  tenderUsers,
}: ChatBubbleBaseProps): JSX.Element => {

  // Hooks
  const { t } = useTranslation(["components", "common"]);
  const intl = useIntl();

  return (
    <Card
      className={cn("py-3 px-4 max-w-[49%] md:min-w-75 xl:min-w-125 shadow-md", getDecisionColour(decision), getBackgroundColour(decision))}
    >
      {/* Top container */}
      <div className="flex-row md:flex justify-between">
        <div className="flex items-center gap-2">
          {/* Image */}
          {getUser(decision) && (
            <UserAvatar user={getUser(decision)} />
          )}
          <div>
            {/* User name */}
            <p className="font-semibold text-sm">{getUserName(decision)}</p>
            {/* Organization name */}
            <p className="text-xs text-gray-400 font-light">
              {getOrganizationName(decision) ??
                t("chatbubble.unknownorganization")}
            </p>
          </div>
        </div>
        {/* Time/Date stamp */}
        <div className="flex justify-end">
          <p className="text-xs text-gray-400 font-light whitespace-nowrap">
            {formatDateLocale(intl, getDate(decision), {
              year: "numeric",
              month: "numeric",
              day: "numeric",
              hour: "numeric",
              minute: "numeric",
            })}
          </p>
        </div>
      </div>

      {/* Title */}
      <div className="mt-2">
        <p className="font-semibold text-sm">{getTitleTextPrefix(decision)}</p>
      </div>

      {/* Comment with mention transformation */}
      <div className="text-sm text-gray-700 font-light">
        {tenderUsers
          ? ChatMessageWithMentions(getComment(decision), tenderUsers)
          : getComment(decision)}
      </div>

      {/* Additional Content */}
      {additionalContent}
    </Card>
  );
};

export default ChatBubbleBase;
